<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import InternalChatChannelsAPI from 'dashboard/api/internalChatChannels';

// ─── Props ────────────────────────────────────────────────────────────────────
const props = defineProps({
  channel: {
    type: Object,
    default: () => ({}),
  },
  currentUserId: {
    type: Number,
    default: 0,
  },
  canUpdate: {
    type: Boolean,
    default: false,
  },
  canArchive: {
    type: Boolean,
    default: false,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  canInviteMembers: {
    type: Boolean,
    default: false,
  },
  canRemoveMembers: {
    type: Boolean,
    default: false,
  },
});

// ─── Emits ────────────────────────────────────────────────────────────────────
const emit = defineEmits([
  'close',
  'leave',
  'closeDm',
  'archive',
  'unarchive',
  'delete',
  'toggleFavorite',
  'toggleMute',
  'editMembers',
]);

// ─── Composables ─────────────────────────────────────────────────────────────
const { t } = useI18n();
const store = useStore();

// ─── Internal state ───────────────────────────────────────────────────────────
const isVisible = ref(false);
const activeTab = ref('about');
const memberSearch = ref('');
const showNotifDropdown = ref(false);
const showAdvancedNotif = ref(false);
const deleteDialogRef = ref(null);
const editingName = ref(false);
const editingDescription = ref(false);
const editedName = ref('');
const editedDescription = ref('');
const nameInputRef = ref(null);
const descInputRef = ref(null);
const advBadgeEnabled = ref(true);
const advFollowAll = ref(true);
const removingMemberId = ref(null);
const isTogglingPrivacy = ref(false);
const showArchiveConfirmOverlay = ref(false);
const memberToRemove = ref(null);

// ─── Computed ─────────────────────────────────────────────────────────────────
const isDM = computed(() => props.channel.channel_type === 'dm');
const isPrivate = computed(() => props.channel.channel_type === 'private_channel');
const isArchived = computed(() => props.channel.status === 'archived');
const isMuted = computed(() => !!props.channel.muted);
const isFavorited = computed(() => !!props.channel.favorited);

const dmPeer = computed(() => {
  if (!isDM.value) return null;
  const members = props.channel.members || [];
  return members.find(m => m.user_id !== props.currentUserId) || null;
});

const channelName = computed(() => {
  if (isDM.value && dmPeer.value) return dmPeer.value.name;
  return props.channel.name || '';
});

const channelIcon = computed(() => {
  if (isPrivate.value) return 'i-lucide-lock';
  return 'i-lucide-hash';
});

const memberCount = computed(
  () => props.channel.members?.length || props.channel.members_count || 0
);

const filteredMembers = computed(() => {
  const members = props.channel.members || [];
  if (!memberSearch.value) return members;
  const q = memberSearch.value.toLowerCase();
  return members.filter(m => (m.name || '').toLowerCase().includes(q));
});

const createdByMember = computed(() => {
  const members = props.channel.members || [];
  return members.find(m => m.user_id === props.channel.created_by_id) || null;
});

const createdAtFormatted = computed(() => {
  const val = props.channel.created_at;
  if (!val) return '';
  const d = typeof val === 'string' ? new Date(val) : new Date(val * 1000);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});

const notifLabel = computed(() =>
  isMuted.value
    ? t('INTERNAL_CHAT.MODAL.NOTIF.MUTE_SHORT')
    : t('INTERNAL_CHAT.MODAL.NOTIF.ALL_SHORT')
);

const tabs = computed(() => {
  const memberLabel = memberCount.value > 0
    ? `${t('INTERNAL_CHAT.MODAL.TAB_MEMBERS')} ${memberCount.value}`
    : t('INTERNAL_CHAT.MODAL.TAB_MEMBERS');

  return [
    { key: 'about', label: t('INTERNAL_CHAT.MODAL.TAB_ABOUT') },
    { key: 'members', label: memberLabel.trim() },
    { key: 'integrations', label: t('INTERNAL_CHAT.MODAL.TAB_INTEGRATIONS') },
    { key: 'settings', label: t('INTERNAL_CHAT.MODAL.TAB_SETTINGS') },
  ];
});

// ─── Functions ────────────────────────────────────────────────────────────────
function open(tab = 'about') {
  activeTab.value = tab;
  editingName.value = false;
  editingDescription.value = false;
  editedName.value = '';
  editedDescription.value = '';
  isVisible.value = true;
}

function close() {
  isVisible.value = false;
  emit('close');
}

function handleKeydown(e) {
  if (e.key !== 'Escape') return;
  if (showAdvancedNotif.value) {
    showAdvancedNotif.value = false;
    return;
  }
  if (showArchiveConfirmOverlay.value) {
    showArchiveConfirmOverlay.value = false;
    memberToRemove.value = null;
    return;
  }
  if (showNotifDropdown.value) {
    showNotifDropdown.value = false;
    return;
  }
  if (isVisible.value) {
    close();
  }
}

function setNotifLevel(level) {
  showNotifDropdown.value = false;
  if (level === 'mute' && !isMuted.value) {
    emit('toggleMute');
  } else if (level !== 'mute' && isMuted.value) {
    emit('toggleMute');
  }
}

async function saveChannelName() {
  const name = editedName.value.trim();
  if (!name) return;
  try {
    await store.dispatch('internalChat/update', {
      channelId: props.channel.id,
      channel: { name },
    });
  } catch {
    // handled by store
  }
  editingName.value = false;
}

async function saveChannelDescription() {
  const description = editedDescription.value.trim();
  try {
    await store.dispatch('internalChat/update', {
      channelId: props.channel.id,
      channel: { description },
    });
  } catch {
    // handled by store
  }
  editingDescription.value = false;
}

function startEditName() {
  editedName.value = props.channel.name || '';
  editingName.value = true;
  nextTick(() => {
    nameInputRef.value?.focus();
  });
}

function startEditDescription() {
  editedDescription.value = props.channel.description || '';
  editingDescription.value = true;
  nextTick(() => {
    descInputRef.value?.focus();
  });
}

function copyChannelId() {
  navigator.clipboard.writeText(String(props.channel.id));
  useAlert(t('INTERNAL_CHAT.MODAL.ID_COPIED'));
}

async function togglePrivacy() {
  if (isTogglingPrivacy.value) return;
  isTogglingPrivacy.value = true;
  try {
    await store.dispatch('internalChat/update', {
      channelId: props.channel.id,
      channel: {
        channel_type: isPrivate.value ? 'public_channel' : 'private_channel',
      },
    });
  } catch {
    // handled by store
  } finally {
    isTogglingPrivacy.value = false;
  }
}

function handleRemoveMember(member) {
  const members = props.channel.members || [];
  if (members.length === 1) {
    memberToRemove.value = member;
    showArchiveConfirmOverlay.value = true;
    return;
  }
  doRemoveMember(member);
}

async function doRemoveMember(member) {
  if (removingMemberId.value) return;
  removingMemberId.value = member.id;
  try {
    await InternalChatChannelsAPI.removeMember(props.channel.id, member.id);
    await store.dispatch('internalChat/show', props.channel.id);
  } catch {
    // silently handled
  } finally {
    removingMemberId.value = null;
  }
}

function confirmRemoveAndArchive() {
  showArchiveConfirmOverlay.value = false;
  memberToRemove.value = null;
  close();
  emit('archive');
}

function handleLeave() {
  close();
  emit(isDM.value ? 'closeDm' : 'leave');
}

function handleArchive() {
  close();
  emit(isArchived.value ? 'unarchive' : 'archive');
}

function handleDelete() {
  deleteDialogRef.value?.open();
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
});

// ─── Expose ───────────────────────────────────────────────────────────────────
defineExpose({ open });
</script>

<template>
  <!-- Delete confirmation dialog — outside Teleport so it always mounts -->
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('INTERNAL_CHAT.CHANNEL.DELETE')"
    :description="t('INTERNAL_CHAT.CHANNEL.CONFIRM_DELETE')"
    :confirm-button-label="t('INTERNAL_CHAT.CHANNEL.DELETE')"
    @confirm="emit('delete')"
  />

  <Teleport to="body">
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/40 backdrop-blur-sm"
        @click="close"
      />

      <!-- Modal container -->
      <div
        class="relative z-10 flex w-full max-w-xl flex-col rounded-2xl bg-n-solid-2 shadow-2xl"
        style="height: min(580px, 85vh)"
      >

        <!-- ───────────────────────── HEADER ───────────────────────────── -->
        <div class="flex-shrink-0 border-b border-n-slate-5 px-6 pb-0 pt-5">

          <!-- Title row -->
          <div class="mb-3 flex items-center gap-3">
            <!-- DM: peer avatar; Channel: icon -->
            <Avatar
              v-if="isDM && dmPeer"
              :name="dmPeer.name || ''"
              :src="dmPeer.avatar_url || ''"
              :status="dmPeer.availability_status"
              :size="32"
              rounded-full
            />
            <div
              v-else-if="!isDM"
              class="flex size-8 flex-shrink-0 items-center justify-center rounded-lg bg-n-alpha-1"
            >
              <Icon :icon="channelIcon" class="size-4 text-n-slate-11" />
            </div>

            <h2 class="flex-1 truncate text-base font-semibold text-n-slate-12">
              {{ channelName }}
            </h2>

            <!-- Close button -->
            <button
              class="flex-shrink-0 rounded-lg p-1.5 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="t('INTERNAL_CHAT.THREAD.CLOSE')"
              @click="close"
            >
              <Icon icon="i-lucide-x" class="size-4" />
            </button>
          </div>

          <!-- Action row (non-DM): favorite + notification dropdown -->
          <div v-if="!isDM" class="mb-3 flex items-center gap-2">
            <!-- Favorite -->
            <button
              class="flex items-center gap-1.5 rounded-lg border border-n-slate-5 px-3 py-1.5 text-xs transition-colors hover:bg-n-alpha-2"
              :class="isFavorited ? 'text-n-amber-9' : 'text-n-slate-11'"
              @click="emit('toggleFavorite')"
            >
              <Icon
                icon="i-lucide-star"
                class="size-3.5"
                :class="isFavorited ? 'fill-current' : ''"
              />
              {{ isFavorited ? t('INTERNAL_CHAT.CHANNEL.UNFAVORITE') : t('INTERNAL_CHAT.CHANNEL.FAVORITE') }}
            </button>

            <!-- Notification dropdown trigger -->
            <div class="relative">
              <button
                class="flex items-center gap-1.5 rounded-lg border border-n-slate-5 px-3 py-1.5 text-xs text-n-slate-11 transition-colors hover:bg-n-alpha-2"
                :class="{ 'bg-n-alpha-2': showNotifDropdown }"
                @click="showNotifDropdown = !showNotifDropdown"
              >
                <Icon
                  :icon="isMuted ? 'i-lucide-bell-off' : 'i-lucide-bell'"
                  class="size-3.5"
                />
                {{ notifLabel }}
                <Icon icon="i-lucide-chevron-down" class="size-3" />
              </button>

              <!-- Notif dropdown backdrop -->
              <div
                v-if="showNotifDropdown"
                class="fixed inset-0 z-10"
                @click="showNotifDropdown = false"
              />

              <!-- Notif dropdown panel -->
              <div
                v-if="showNotifDropdown"
                class="absolute left-0 top-full z-20 mt-1 w-72 rounded-xl border border-n-slate-5 bg-n-solid-2 py-2 shadow-lg"
              >
                <p class="px-3 pb-2 text-xs text-n-slate-10">
                  {{ t('INTERNAL_CHAT.MODAL.NOTIF.LABEL') }}
                </p>

                <!-- All new posts -->
                <button
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  @click="setNotifLevel('all')"
                >
                  <Icon icon="i-lucide-bell" class="size-4 text-n-slate-11 flex-shrink-0" />
                  <span class="flex-1 text-left">{{ t('INTERNAL_CHAT.MODAL.NOTIF.ALL') }}</span>
                  <Icon
                    v-if="!isMuted"
                    icon="i-lucide-check"
                    class="size-4 text-n-blue-9 flex-shrink-0"
                  />
                </button>

                <!-- Mentions only -->
                <button
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  @click="setNotifLevel('mentions')"
                >
                  <Icon icon="i-lucide-at-sign" class="size-4 text-n-slate-11 flex-shrink-0" />
                  <span class="flex-1 text-left">{{ t('INTERNAL_CHAT.MODAL.NOTIF.MENTIONS') }}</span>
                </button>

                <!-- Mute and hide -->
                <button
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  @click="setNotifLevel('mute')"
                >
                  <Icon icon="i-lucide-bell-off" class="size-4 text-n-slate-11 flex-shrink-0" />
                  <span class="flex-1 text-left">{{ t('INTERNAL_CHAT.MODAL.NOTIF.MUTE') }}</span>
                  <Icon
                    v-if="isMuted"
                    icon="i-lucide-check"
                    class="size-4 text-n-blue-9 flex-shrink-0"
                  />
                </button>

                <div class="my-1 h-px bg-n-slate-4" />

                <!-- Advanced options -->
                <button
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  @click="showNotifDropdown = false; showAdvancedNotif = true"
                >
                  <Icon icon="i-lucide-settings-2" class="size-4 text-n-slate-11 flex-shrink-0" />
                  <span class="flex-1 text-left">{{ t('INTERNAL_CHAT.MODAL.NOTIF.ADVANCED') }}</span>
                  <Icon icon="i-lucide-chevron-right" class="size-4 text-n-slate-10 flex-shrink-0" />
                </button>

                <!-- Edit default preferences -->
                <button
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  @click="showNotifDropdown = false"
                >
                  <Icon icon="i-lucide-sliders-horizontal" class="size-4 text-n-slate-11 flex-shrink-0" />
                  <span class="flex-1 text-left">{{ t('INTERNAL_CHAT.MODAL.NOTIF.EDIT_DEFAULTS') }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Tabs row -->
          <div class="-mb-px mt-1 flex gap-0">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              class="border-b-2 px-3 pb-2.5 text-sm font-medium transition-colors"
              :class="
                activeTab === tab.key
                  ? 'border-n-blue-9 text-n-blue-9'
                  : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
              "
              @click="activeTab = tab.key"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <!-- ─────────────────────── TAB CONTENT ───────────────────────── -->
        <div class="flex-1 min-h-0">

          <!-- ══════════════ ABOUT ══════════════ -->
          <div v-if="activeTab === 'about'" class="h-full overflow-y-auto space-y-5 px-6 py-5">

            <!-- Channel name (non-DM) -->
            <div v-if="!isDM" class="flex flex-col gap-1">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                {{ t('INTERNAL_CHAT.MODAL.CHANNEL_NAME_LABEL') }}
              </span>
              <!-- Display mode -->
              <div v-if="!editingName" class="group flex items-center gap-2">
                <div class="flex flex-1 items-center gap-2 text-sm text-n-slate-12">
                  <Icon :icon="channelIcon" class="size-4 flex-shrink-0 text-n-slate-10" />
                  {{ channelName }}
                </div>
                <button
                  v-if="canUpdate"
                  class="rounded-lg p-1 text-n-slate-10 opacity-0 transition-opacity hover:bg-n-alpha-2 hover:text-n-slate-12 group-hover:opacity-100"
                  @click="startEditName"
                >
                  <Icon icon="i-lucide-pencil" class="size-3.5" />
                </button>
              </div>
              <!-- Edit mode -->
              <div v-else class="flex items-center gap-2">
                <input
                  ref="nameInputRef"
                  v-model="editedName"
                  type="text"
                  class="flex-1 rounded-lg border border-n-slate-5 bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:ring-2 focus:ring-n-blue-9"
                  @keydown.enter="saveChannelName"
                  @keydown.esc="editingName = false"
                />
                <button
                  class="rounded-lg bg-n-blue-9 px-3 py-1.5 text-xs font-medium text-white"
                  @click="saveChannelName"
                >
                  {{ t('INTERNAL_CHAT.MODAL.SAVE') }}
                </button>
                <button
                  class="rounded-lg px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                  @click="editingName = false"
                >
                  {{ t('INTERNAL_CHAT.MODAL.CANCEL') }}
                </button>
              </div>
            </div>

            <!-- Description -->
            <div class="flex flex-col gap-1">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                {{ t('INTERNAL_CHAT.MODAL.DESCRIPTION_LABEL') }}
              </span>
              <!-- Display mode -->
              <div v-if="!editingDescription" class="group flex items-start gap-2">
                <p class="flex-1 text-sm text-n-slate-12 leading-relaxed">
                  <span v-if="channel.description">{{ channel.description }}</span>
                  <span v-else class="italic text-n-slate-10">
                    {{ t('INTERNAL_CHAT.MODAL.DESCRIPTION_PLACEHOLDER') }}
                  </span>
                </p>
                <button
                  v-if="canUpdate"
                  class="mt-0.5 rounded-lg p-1 text-n-slate-10 opacity-0 transition-opacity hover:bg-n-alpha-2 hover:text-n-slate-12 group-hover:opacity-100"
                  @click="startEditDescription"
                >
                  <Icon icon="i-lucide-pencil" class="size-3.5" />
                </button>
              </div>
              <!-- Edit mode -->
              <div v-else class="flex flex-col gap-2">
                <textarea
                  ref="descInputRef"
                  v-model="editedDescription"
                  rows="3"
                  class="w-full resize-none rounded-lg border border-n-slate-5 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:ring-2 focus:ring-n-blue-9"
                  @keydown.esc="editingDescription = false"
                />
                <div class="flex gap-2">
                  <button
                    class="rounded-lg bg-n-blue-9 px-3 py-1.5 text-xs font-medium text-white"
                    @click="saveChannelDescription"
                  >
                    {{ t('INTERNAL_CHAT.MODAL.SAVE') }}
                  </button>
                  <button
                    class="rounded-lg px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                    @click="editingDescription = false"
                  >
                    {{ t('INTERNAL_CHAT.MODAL.CANCEL') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Created by -->
            <div v-if="createdByMember" class="flex flex-col gap-1">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                {{ t('INTERNAL_CHAT.MODAL.CREATED_BY') }}
              </span>
              <div class="flex items-center gap-2">
                <Avatar
                  :name="createdByMember.name || ''"
                  :src="createdByMember.avatar_url || ''"
                  :size="24"
                  rounded-full
                />
                <div class="flex flex-col">
                  <span class="text-sm text-n-slate-12">{{ createdByMember.name }}</span>
                  <span v-if="createdAtFormatted" class="text-xs text-n-slate-10">{{ createdAtFormatted }}</span>
                </div>
              </div>
            </div>

            <div class="h-px bg-n-slate-4" />

            <!-- Leave / Close DM -->
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-n-ruby-11 transition-colors hover:bg-n-ruby-3"
              @click="handleLeave"
            >
              <Icon
                :icon="isDM ? 'i-lucide-x-circle' : 'i-lucide-log-out'"
                class="size-4"
              />
              {{ isDM ? t('INTERNAL_CHAT.CHANNEL.CLOSE_DM') : t('INTERNAL_CHAT.CHANNEL.LEAVE') }}
            </button>

            <!-- Channel ID -->
            <div class="flex flex-col gap-1">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                {{ t('INTERNAL_CHAT.MODAL.CHANNEL_ID_LABEL') }}
              </span>
              <div class="flex items-center gap-2">
                <code class="flex-1 rounded-lg bg-n-alpha-1 px-3 py-1.5 font-mono text-xs text-n-slate-12">
                  {{ channel.id }}
                </code>
                <button
                  class="rounded-lg p-1.5 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :title="t('INTERNAL_CHAT.MODAL.ID_COPIED')"
                  @click="copyChannelId"
                >
                  <Icon icon="i-lucide-copy" class="size-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- ══════════════ MEMBERS ══════════════ -->
          <div v-else-if="activeTab === 'members'" class="flex h-full flex-col px-6 py-5">
            <!-- Search + Add people -->
            <div class="mb-4 flex gap-2">
              <input
                v-model="memberSearch"
                type="text"
                class="h-9 flex-1 rounded-lg border border-n-slate-5 bg-n-solid-1 px-3 text-sm text-n-slate-12 placeholder-n-slate-10 outline-none focus:ring-2 focus:ring-n-blue-9"
                :placeholder="t('INTERNAL_CHAT.MODAL.SEARCH_MEMBERS')"
              />
              <button
                v-if="!isDM && canInviteMembers"
                type="button"
                class="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg bg-n-blue-9 px-3 text-sm font-medium text-white transition-colors hover:bg-n-blue-10"
                @click="emit('editMembers')"
              >
                <Icon icon="i-lucide-user-plus" class="size-4" />
                {{ t('INTERNAL_CHAT.MODAL.ADD_PEOPLE') }}
              </button>
            </div>

            <!-- Member list -->
            <ul class="flex-1 min-h-0 overflow-y-auto space-y-1">
              <li
                v-for="member in filteredMembers"
                :key="member.id"
                class="group flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-n-alpha-2"
              >
                <Avatar
                  :name="member.name || ''"
                  :src="member.avatar_url || ''"
                  :status="member.availability_status"
                  :size="32"
                  rounded-full
                />
                <div class="flex flex-1 items-center gap-2 min-w-0">
                  <span class="truncate text-sm font-medium text-n-slate-12">
                    {{ member.name }}
                  </span>
                  <span
                    v-if="member.user_id === currentUserId"
                    class="flex-shrink-0 rounded bg-n-alpha-1 px-1.5 py-0.5 text-xs text-n-slate-10"
                  >
                    {{ t('INTERNAL_CHAT.MODAL.YOU') }}
                  </span>
                  <span
                    v-if="member.role === 'admin'"
                    class="flex-shrink-0 rounded bg-n-blue-3 px-1.5 py-0.5 text-xs font-medium text-n-blue-11"
                  >
                    {{ t('INTERNAL_CHAT.CHANNEL.ADMIN') }}
                  </span>
                </div>
                <button
                  v-if="canRemoveMembers && member.user_id !== currentUserId"
                  type="button"
                  class="invisible flex-shrink-0 text-xs text-n-ruby-11 transition-opacity hover:underline group-hover:visible"
                  :disabled="removingMemberId === member.id"
                  @click.stop="handleRemoveMember(member)"
                >
                  {{ removingMemberId === member.id ? '...' : 'Remover' }}
                </button>
              </li>
            </ul>

            <!-- Empty state -->
            <div
              v-if="filteredMembers.length === 0"
              class="py-8 text-center text-sm text-n-slate-10"
            >
              {{ t('INTERNAL_CHAT.MODAL.NO_MEMBERS_FOUND') }}
            </div>
          </div>

          <!-- ══════════════ INTEGRATIONS ══════════════ -->
          <div
            v-else-if="activeTab === 'integrations'"
            class="flex h-full flex-col items-center justify-center px-6 text-center"
          >
            <div class="mb-3 flex size-12 items-center justify-center rounded-2xl bg-n-alpha-1">
              <Icon icon="i-lucide-plug" class="size-6 text-n-slate-10" />
            </div>
            <p class="text-sm font-medium text-n-slate-11">
              {{ t('INTERNAL_CHAT.MODAL.INTEGRATIONS_COMING_SOON') }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('INTERNAL_CHAT.MODAL.INTEGRATIONS_SUBTITLE') }}
            </p>
          </div>

          <!-- ══════════════ SETTINGS ══════════════ -->
          <div v-else-if="activeTab === 'settings'" class="h-full overflow-y-auto space-y-6 px-6 py-5">

            <!-- Channel name (admin, non-DM) -->
            <div v-if="!isDM && canUpdate" class="flex flex-col gap-1">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                {{ t('INTERNAL_CHAT.MODAL.CHANNEL_NAME_LABEL') }}
              </span>
              <div v-if="!editingName" class="group flex items-center gap-2">
                <div class="flex flex-1 items-center gap-2 text-sm text-n-slate-12">
                  <Icon :icon="channelIcon" class="size-4 flex-shrink-0 text-n-slate-10" />
                  {{ channelName }}
                </div>
                <button
                  class="rounded-lg p-1 text-n-slate-10 opacity-0 transition-opacity hover:bg-n-alpha-2 hover:text-n-slate-12 group-hover:opacity-100"
                  @click="startEditName"
                >
                  <Icon icon="i-lucide-pencil" class="size-3.5" />
                </button>
              </div>
              <div v-else class="flex items-center gap-2">
                <input
                  ref="nameInputRef"
                  v-model="editedName"
                  type="text"
                  class="flex-1 rounded-lg border border-n-slate-5 bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:ring-2 focus:ring-n-blue-9"
                  @keydown.enter="saveChannelName"
                  @keydown.esc="editingName = false"
                />
                <button
                  class="rounded-lg bg-n-blue-9 px-3 py-1.5 text-xs font-medium text-white"
                  @click="saveChannelName"
                >
                  {{ t('INTERNAL_CHAT.MODAL.SAVE') }}
                </button>
                <button
                  class="rounded-lg px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                  @click="editingName = false"
                >
                  {{ t('INTERNAL_CHAT.MODAL.CANCEL') }}
                </button>
              </div>
            </div>

            <!-- Privacy (admin, non-DM) -->
            <div v-if="!isDM && canUpdate" class="flex flex-col gap-1">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                {{ t('INTERNAL_CHAT.MODAL.PRIVACY_LABEL') }}
              </span>
              <div class="flex items-center gap-2 rounded-xl bg-n-alpha-1 px-3 py-2.5">
                <Icon
                  :icon="isPrivate ? 'i-lucide-lock' : 'i-lucide-hash'"
                  class="size-4 flex-shrink-0 text-n-slate-11"
                />
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ isPrivate ? t('INTERNAL_CHAT.CHANNEL.PRIVATE') : t('INTERNAL_CHAT.CHANNEL.PUBLIC') }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ isPrivate ? t('INTERNAL_CHAT.MODAL.PRIVACY_PRIVATE_DESC') : t('INTERNAL_CHAT.MODAL.PRIVACY_PUBLIC_DESC') }}
                  </p>
                </div>
                <button
                  type="button"
                  class="flex-shrink-0 rounded-lg px-2.5 py-1 text-xs font-medium text-n-blue-9 transition-colors hover:bg-n-blue-3 disabled:opacity-50"
                  :disabled="isTogglingPrivacy"
                  @click="togglePrivacy"
                >
                  {{ isTogglingPrivacy ? '...' : (isPrivate ? t('INTERNAL_CHAT.CHANNEL.PUBLIC') : t('INTERNAL_CHAT.CHANNEL.PRIVATE')) }}
                </button>
              </div>
            </div>

            <!-- Divider (non-DM) -->
            <div v-if="!isDM" class="h-px bg-n-slate-4" />

            <!-- Danger zone (admin, non-DM) -->
            <div v-if="!isDM && (canArchive || canDelete)" class="flex flex-col gap-2">
              <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
                {{ t('INTERNAL_CHAT.MODAL.DANGER_ZONE') }}
              </span>

              <!-- Archive -->
              <button
                v-if="canArchive"
                type="button"
                class="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2"
                @click="handleArchive"
              >
                <Icon
                  :icon="isArchived ? 'i-lucide-archive-restore' : 'i-lucide-archive'"
                  class="size-4 text-n-slate-11"
                />
                {{ isArchived ? t('INTERNAL_CHAT.CHANNEL.UNARCHIVE') : t('INTERNAL_CHAT.CHANNEL.ARCHIVE') }}
              </button>

              <!-- Delete -->
              <button
                v-if="canDelete"
                type="button"
                class="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-n-ruby-11 transition-colors hover:bg-n-ruby-3"
                @click="handleDelete"
              >
                <Icon icon="i-lucide-trash-2" class="size-4" />
                {{ t('INTERNAL_CHAT.CHANNEL.DELETE') }}
              </button>
            </div>
          </div>
        </div>

        <!-- ──────── ARCHIVE CONFIRM OVERLAY ──────── -->
        <div
          v-if="showArchiveConfirmOverlay"
          class="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-black/40 p-4"
        >
          <div class="w-full max-w-sm rounded-2xl border border-n-slate-5 bg-n-solid-2 shadow-2xl">
            <div class="flex items-start gap-3 px-5 pb-3 pt-5">
              <div class="flex-1">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Sair de <span class="font-semibold">{{ channelName }}</span> e arquivar o canal
                </h3>
              </div>
              <button
                class="rounded-lg p-1.5 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
                @click="showArchiveConfirmOverlay = false; memberToRemove = null"
              >
                <Icon icon="i-lucide-x" class="size-4" />
              </button>
            </div>

            <div class="space-y-3 px-5 pb-5">
              <p class="text-xs text-n-slate-11">
                Arquivar é útil se você acha que não precisará mais do canal. Veja aqui o que acontecerá ao arquivar o canal:
              </p>
              <ul class="space-y-1.5 text-xs text-n-slate-11">
                <li class="flex gap-2"><span class="flex-shrink-0">•</span><span>Ninguém poderá enviar mensagens para ele</span></li>
                <li class="flex gap-2"><span class="flex-shrink-0">•</span><span>Os apps instalados no canal serão desativados</span></li>
                <li class="flex gap-2"><span class="flex-shrink-0">•</span><span>Uma mensagem será postada informando que você arquivou o canal</span></li>
                <li class="flex gap-2"><span class="flex-shrink-0">•</span><span>As mensagens de canais arquivados ainda aparecerão na pesquisa e nos arquivos. Você poderá desarquivar um canal arquivado mais tarde caso precise dele novamente.</span></li>
              </ul>
              <p class="text-xs font-medium text-n-slate-12">
                Tem certeza de que quer arquivar <span class="font-semibold">{{ channelName }}</span>?
              </p>
              <div class="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  class="rounded-lg px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                  @click="showArchiveConfirmOverlay = false; memberToRemove = null"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="rounded-lg bg-n-ruby-9 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-n-ruby-10"
                  @click="confirmRemoveAndArchive"
                >
                  Sim, sair do canal e arquivá-lo
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ──────── ADVANCED NOTIF OVERLAY (inside modal) ──────── -->
        <div
          v-if="showAdvancedNotif"
          class="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-black/40"
        >
          <div class="w-full max-w-sm rounded-2xl border border-n-slate-5 bg-n-solid-2 shadow-2xl">
            <!-- Secondary modal header -->
            <div class="flex items-start gap-3 px-5 pb-3 pt-5">
              <div class="flex-1">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ t('INTERNAL_CHAT.MODAL.NOTIF.ADVANCED_TITLE') }}
                </h3>
                <p class="mt-0.5 text-xs text-n-slate-10">
                  {{ t('INTERNAL_CHAT.MODAL.NOTIF.ADVANCED_SUBTITLE') }}
                </p>
              </div>
              <button
                class="rounded-lg p-1.5 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
                @click="showAdvancedNotif = false"
              >
                <Icon icon="i-lucide-x" class="size-4" />
              </button>
            </div>

            <div class="space-y-1 px-5 pb-5">
              <!-- Badge toggle -->
              <label class="flex cursor-pointer items-center justify-between gap-4 rounded-xl py-2.5">
                <span class="text-sm text-n-slate-12">
                  {{ t('INTERNAL_CHAT.MODAL.NOTIF.BADGE_LABEL') }}
                </span>
                <Switch v-model="advBadgeEnabled" class="flex-shrink-0" />
              </label>

              <!-- Follow all toggle -->
              <label class="flex cursor-pointer items-center justify-between gap-4 rounded-xl py-2.5">
                <span class="text-sm text-n-slate-12">
                  {{ t('INTERNAL_CHAT.MODAL.NOTIF.FOLLOW_ALL_LABEL') }}
                </span>
                <Switch v-model="advFollowAll" class="flex-shrink-0" />
              </label>
            </div>
          </div>
        </div>

      </div>
    </div>
  </Teleport>
</template>
